import { useState } from 'react';
import { ChevronLeft, Check, Loader2, Pencil, X } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { extractErrorMessage } from '../../api/errors';
import {
  AffiliateCommission,
  AffiliateReferral,
  useAffiliate,
  useRetryCommission,
  useStartStripeOnboarding,
  useStripeConnectStatus,
} from '../../queries/affiliates';
import { AffiliateFormDialog } from './AffiliateFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Badge, BadgeProps } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const money = (v: string | number) => `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const DATE = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' });
const DATETIME = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const fmt = (v: string | null) => (v ? DATE.format(new Date(v)) : '—');

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = { paid: 'success', pending: 'warning', failed: 'destructive', reversed: 'outline', cancelled: 'outline' };

export function AffiliateDetailPage() {
  const { affiliateId = '' } = useParams();
  const { data, isLoading } = useAffiliate(affiliateId);
  const [editing, setEditing] = useState(false);
  const retry = useRetryCommission(affiliateId);
  const { success, error } = useToast();
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const handleRetry = (commissionId: number) => {
    setRetryingId(commissionId);
    retry.mutate(commissionId, {
      onSuccess: () => success('Payout re-attempted successfully.'),
      onError: (err) => error(extractErrorMessage(err, 'Retry failed.')),
      onSettled: () => setRetryingId(null),
    });
  };

  if (isLoading || !data) {
    return <Spinner />;
  }

  const s = data.commissionSummary;
  const rate = data.commission_type === 'percentage' ? `${Number(data.commission_value)}%` : money(data.commission_value);

  const commissionColumns: DataTableColumn<AffiliateCommission>[] = [
    { key: 'date', header: 'Date', priority: true, render: (c) => <span className="text-muted-foreground @2xl:text-foreground">{c.payment_date ? DATETIME.format(new Date(c.payment_date)) : '—'}</span> },
    { key: 'customer', header: 'Customer', priority: false, render: (c) => (c.user_id ? <Link to={`/users/${c.user_id}`} className="text-primary hover:underline">User {c.user_id}</Link> : '—') },
    { key: 'level', header: 'Level', priority: false, render: (c) => <span className="text-muted-foreground">{c.commission_level}</span> },
    { key: 'payment', header: 'Payment #', priority: false, render: (c) => <span className="text-muted-foreground">{c.payment_number ?? '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      priority: true,
      render: (c) => (
        <span className="inline-flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[c.status] ?? 'outline'}>{c.status}</Badge>
          {(c.retriable ?? c.status === 'failed') && (
            <Button variant="outline" size="sm" onClick={() => handleRetry(c.id)} disabled={retryingId === c.id}>
              {retryingId === c.id && <Loader2 className="h-3 w-3 animate-spin" />}
              Retry
            </Button>
          )}
        </span>
      ),
    },
    { key: 'transfer', header: 'Transfer', priority: false, render: (c) => <span className="font-mono text-xs text-muted-foreground">{c.stripe_transfer_id ?? '—'}</span> },
    {
      key: 'amount',
      header: 'Amount',
      priority: true,
      alignEnd: true,
      render: (c) => (
        <span className="inline-flex flex-col items-end">
          <span className={`font-medium tabular-nums ${c.status === 'cancelled' ? 'text-muted-foreground line-through' : ''}`}>{money(c.commission_amount)}</span>
          {Number(c.reversed_amount) > 0 && <span className="text-xs tabular-nums text-muted-foreground">{money(c.reversed_amount)} clawed back</span>}
        </span>
      ),
    },
  ];

  const referralColumns: DataTableColumn<AffiliateReferral>[] = [
    { key: 'referred', header: 'Referred', priority: true, render: (r) => <span className="text-muted-foreground @2xl:text-foreground">{fmt(r.created_at)}</span> },
    { key: 'customer', header: 'Customer', priority: true, render: (r) => <Link to={`/users/${r.user_id}`} className="text-primary hover:underline">User {r.user_id}</Link> },
    { key: 'code', header: 'Tracking code', priority: false, render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.tracking_code}</span> },
    { key: 'converted', header: 'Converted', priority: false, render: (r) => (r.converted_at ? <span className="text-emerald-600 dark:text-emerald-400">{fmt(r.converted_at)}</span> : <span className="text-muted-foreground">—</span>) },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Button asChild variant="outline" size="sm">
          <Link to="/affiliates">
            <ChevronLeft className="h-4 w-4" /> Affiliates
          </Link>
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="h-4 w-4" /> Edit
        </Button>
      </div>
      {editing && <AffiliateFormDialog affiliate={data} onClose={() => setEditing(false)} />}

      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{data.display_name || data.affiliate_code}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-mono">{data.affiliate_code}</span> · {data.status} · {rate} {data.commission_type === 'percentage' ? 'commission' : 'flat'} · {data.payment_schedule}
          {data.stripe_account_id ? <> · Stripe <span className="font-mono text-xs">{data.stripe_account_id}</span></> : <span className="ml-1 text-amber-600 dark:text-amber-400"> · no Stripe account</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryTile label="Paid" value={money(s.paid.total)} sub={`${s.paid.count} payouts${s.reversed.total > 0 ? ` · ${money(s.reversed.total)} clawed back` : ''}`} accent="text-emerald-600 dark:text-emerald-400" />
        <SummaryTile label="Pending" value={money(s.pending.total)} sub={`${s.pending.count} payouts`} accent="text-amber-600 dark:text-amber-400" />
        <SummaryTile label="Failed" value={money(s.failed.total)} sub={`${s.failed.count} payouts`} accent={s.failed.count > 0 ? 'text-red-600 dark:text-red-400' : undefined} />
      </div>

      <StripeConnectSection affiliateId={affiliateId} />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Commissions</h2>
        <DataTable columns={commissionColumns} data={data.commissions} getRowKey={(c) => String(c.id)} emptyMessage="No commissions recorded." />
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Referrals</h2>
        <DataTable columns={referralColumns} data={data.referrals} getRowKey={(r) => String(r.id)} emptyMessage="No referrals." />
      </section>
    </div>
  );
}

function StripeConnectSection({ affiliateId }: { affiliateId: string }) {
  const { data: status, isLoading } = useStripeConnectStatus(affiliateId);
  const onboard = useStartStripeOnboarding();
  const { error: toastError } = useToast();

  const startOnboarding = () => {
    onboard.mutate(Number(affiliateId), {
      onSuccess: ({ onboardingUrl }) => {
        window.location.href = onboardingUrl; // Stripe-hosted Express onboarding
      },
      onError: () => toastError('Could not start Stripe onboarding.'),
    });
  };

  const flag = (label: string, on: boolean | undefined) => (
    <span className="inline-flex items-center gap-1 text-sm">
      {on ? <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <X className="h-4 w-4 text-muted-foreground" />}
      {label}
    </span>
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">Stripe payouts</CardTitle>
        {!status?.onboarding_completed && (
          <Button size="sm" onClick={startOnboarding} disabled={onboard.isPending}>
            {onboard.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {status?.hasStripeAccount ? 'Continue onboarding' : 'Set up Stripe payouts'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : !status?.hasStripeAccount ? (
          <p className="text-sm text-muted-foreground">No Stripe account connected — the affiliate can't receive payouts yet.</p>
        ) : status.onboarding_completed ? (
          <p className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
            <Check className="h-4 w-4" /> Onboarding complete — payouts enabled.
          </p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {flag('Details submitted', status.details_submitted)}
            {flag('Charges enabled', status.charges_enabled)}
            {flag('Payouts enabled', status.payouts_enabled)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryTile({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent ?? ''}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </Card>
  );
}
